import { useEffect, useState, useCallback } from "react";
import {
  Activity, AlertTriangle, Clock, Filter, ChevronDown, ChevronUp,
  RefreshCw, Trash2, Search, Server, Zap, XCircle, CheckCircle,
  ArrowUpDown, X,
} from "lucide-react";
import { Card, Badge, Button, Input } from "@/components/kit";
import api from "@/lib/api";

const METHOD_COLORS = {
  GET: "bg-emerald-50 text-emerald-600 border-emerald-200",
  POST: "bg-sky-50 text-sky-600 border-sky-200",
  PUT: "bg-amber-50 text-amber-600 border-amber-200",
  DELETE: "bg-red-50 text-red-600 border-red-200",
  PATCH: "bg-violet-50 text-violet-600 border-violet-200",
};

const STATUS_COLORS = {
  2: "text-emerald-600",
  3: "text-sky-600",
  4: "text-amber-600",
  5: "text-red-600",
};

function statusColor(code) {
  const prefix = String(code)[0];
  return STATUS_COLORS[prefix] || "text-slate-500";
}

function StatusBadge({ code }) {
  const prefix = String(code)[0];
  const bg = prefix === "2" ? "bg-emerald-50 text-emerald-600"
    : prefix === "3" ? "bg-sky-50 text-sky-600"
    : prefix === "4" ? "bg-amber-50 text-amber-600"
    : prefix === "5" ? "bg-red-50 text-red-600"
    : "bg-slate-50 text-slate-600";
  return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md ${bg}`}>{code}</span>;
}

function MethodBadge({ method }) {
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${METHOD_COLORS[method] || "bg-slate-50 text-slate-600"}`}>
      {method}
    </span>
  );
}

// ─── Stats Overview ───
function LogStats({ stats, onRefresh }) {
  if (!stats) return null;

  return (
    <div className="space-y-4">
      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-sky-50 grid place-items-center"><Activity className="w-4 h-4 text-sky-500" /></div>
          </div>
          <p className="font-display text-xl font-extrabold">{stats.total_24h?.toLocaleString()}</p>
          <p className="text-[11px] text-slate-400">Request (24 jam)</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-red-50 grid place-items-center"><XCircle className="w-4 h-4 text-red-500" /></div>
          </div>
          <p className="font-display text-xl font-extrabold">{stats.errors_24h}</p>
          <p className="text-[11px] text-slate-400">Error (24 jam)</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-amber-50 grid place-items-center"><AlertTriangle className="w-4 h-4 text-amber-500" /></div>
          </div>
          <p className="font-display text-xl font-extrabold">{stats.error_rate}%</p>
          <p className="text-[11px] text-slate-400">Error Rate</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 grid place-items-center"><Server className="w-4 h-4 text-emerald-500" /></div>
          </div>
          <p className="font-display text-xl font-extrabold">{stats.total_all?.toLocaleString()}</p>
          <p className="text-[11px] text-slate-400">Total (all-time)</p>
        </Card>
      </div>

      {/* Method + Status + Slow */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Method breakdown */}
        <Card className="p-4">
          <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">By Method</h4>
          <div className="space-y-2">
            {stats.method_breakdown?.map((m) => (
              <div key={m.method} className="flex items-center justify-between">
                <MethodBadge method={m.method} />
                <span className="text-xs font-semibold text-slate-600">{m.count}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Status breakdown */}
        <Card className="p-4">
          <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">By Status</h4>
          <div className="space-y-2">
            {stats.status_breakdown?.map((s) => (
              <div key={s.status} className="flex items-center justify-between">
                <StatusBadge code={s.status} />
                <span className="text-xs font-semibold text-slate-600">{s.count}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Slowest endpoints */}
        <Card className="p-4">
          <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">
            <Zap className="w-3 h-3 inline mr-1" /> Slowest
          </h4>
          <div className="space-y-2">
            {stats.slowest?.map((s) => (
              <div key={s.path} className="text-xs">
                <div className="font-mono text-slate-600 truncate" title={s.path}>{s.path}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-amber-600 font-bold">{s.avg_ms}ms</span>
                  <span className="text-slate-400">· max {s.max_ms}ms</span>
                  <span className="text-slate-300">· {s.count}×</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Hourly traffic */}
      {stats.hourly?.length > 0 && (
        <Card className="p-4">
          <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">Traffic / Hour</h4>
          <div className="flex items-end gap-1 h-20">
            {stats.hourly.map((h) => {
              const max = Math.max(...stats.hourly.map((x) => x.count));
              const pct = max > 0 ? (h.count / max) * 100 : 0;
              return (
                <div key={h.hour} className="flex-1 flex flex-col items-center gap-1" title={`${h.hour}: ${h.count} requests`}>
                  <div
                    className="w-full bg-sky-400/30 rounded-t-sm"
                    style={{ height: `${Math.max(pct, 4)}%` }}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex gap-1 mt-1">
            {stats.hourly.map((h, i) => (
              <div key={h.hour} className="flex-1 text-center">
                {i % 4 === 0 && <span className="text-[8px] text-slate-300">{h.hour.slice(11, 13)}h</span>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Log Detail Modal ───
function LogDetail({ log, onClose }) {
  const [showReq, setShowReq] = useState(false);
  const [showResp, setShowResp] = useState(false);
  if (!log) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <MethodBadge method={log.method} />
            <StatusBadge code={log.status} />
            <span className="font-mono text-sm text-slate-700">{log.path}</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        {/* Meta grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="text-xs"><span className="text-slate-400">Time:</span><br /><span className="font-semibold">{new Date(log.created_at).toLocaleString("id-ID")}</span></div>
          <div className="text-xs"><span className="text-slate-400">Duration:</span><br /><span className="font-semibold">{log.duration_ms}ms</span></div>
          <div className="text-xs"><span className="text-slate-400">Client IP:</span><br /><span className="font-semibold font-mono">{log.client_ip || "-"}</span></div>
          <div className="text-xs"><span className="text-slate-400">User Agent:</span><br /><span className="text-[10px] font-mono truncate block">{log.user_agent || "-"}</span></div>
        </div>
        {log.query && (
          <div className="text-xs mb-3"><span className="text-slate-400">Query:</span> <span className="font-mono text-slate-600">{log.query}</span></div>
        )}
        {log.error && (
          <div className="text-xs mb-3 bg-red-50 rounded-lg p-3 border border-red-100">
            <span className="text-red-600 font-bold">Error:</span> <span className="text-red-700">{log.error}</span>
          </div>
        )}

        {/* Request body */}
        {log.req_body && (
          <div className="mb-3">
            <button onClick={() => setShowReq(!showReq)} className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-sky-500">
              Request Body {showReq ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showReq && (
              <pre className="mt-2 p-3 bg-slate-50 rounded-xl text-[11px] font-mono text-slate-600 overflow-x-auto max-h-64 overflow-y-auto border border-slate-100">
                {tryPrettyJSON(log.req_body)}
              </pre>
            )}
          </div>
        )}

        {/* Response body */}
        {log.resp_body && log.resp_body !== "[streaming]" && (
          <div className="mb-3">
            <button onClick={() => setShowResp(!showResp)} className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-sky-500">
              Response Body {showResp ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showResp && (
              <pre className="mt-2 p-3 bg-slate-50 rounded-xl text-[11px] font-mono text-slate-600 overflow-x-auto max-h-64 overflow-y-auto border border-slate-100">
                {tryPrettyJSON(log.resp_body)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function tryPrettyJSON(s) {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

// ─── Main Component ───
export default function AdminLogs() {
  const [subTab, setSubTab] = useState("stats"); // stats | logs
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage] = useState(30);
  const [detail, setDetail] = useState(null);

  // Filters
  const [methodFilter, setMethodFilter] = useState("");
  const [pathFilter, setPathFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const loadStats = useCallback(() => {
    api.get("/admin/api-logs/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const loadLogs = useCallback(() => {
    setLoading(true);
    const params = { limit: perPage, skip: page * perPage };
    if (methodFilter) params.method = methodFilter;
    if (pathFilter) params.path = pathFilter;
    if (statusFilter) {
      params.status_min = Number(statusFilter);
      params.status_max = Number(statusFilter);
    }
    if (searchFilter) params.search = searchFilter;

    api.get("/admin/api-logs", { params }).then((r) => {
      setLogs(r.data.items);
      setTotal(r.data.total);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [page, perPage, methodFilter, pathFilter, statusFilter, searchFilter]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (subTab === "logs") loadLogs(); }, [subTab, loadLogs]);

  const totalPages = Math.ceil(total / perPage);

  const clearOld = async () => {
    if (!confirm("Hapus logs lebih dari 30 hari?")) return;
    try {
      const { data } = await api.delete("/admin/api-logs", { params: { older_than_days: 30 } });
      alert(`Dihapus ${data.deleted} logs.`);
      loadLogs();
      loadStats();
    } catch {}
  };

  const resetFilters = () => {
    setMethodFilter("");
    setPathFilter("");
    setStatusFilter("");
    setSearchFilter("");
    setPage(0);
  };

  const hasFilters = methodFilter || pathFilter || statusFilter || searchFilter;

  return (
    <div className="space-y-4 animate-float-up">
      {/* Sub-tabs */}
      <div className="flex items-center gap-1 bg-slate-50 rounded-xl p-1">
        <button
          onClick={() => setSubTab("stats")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition ${subTab === "stats" ? "bg-white text-sky-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          <Activity className="w-3.5 h-3.5" /> Stats & Overview
        </button>
        <button
          onClick={() => setSubTab("logs")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition ${subTab === "logs" ? "bg-white text-sky-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          <Server className="w-3.5 h-3.5" /> Request Logs
        </button>
      </div>

      {/* Refresh button */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => { loadStats(); if (subTab === "logs") loadLogs(); }}>
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      {subTab === "stats" && <LogStats stats={stats} />}

      {subTab === "logs" && (
        <div className="space-y-3">
          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border transition ${showFilters ? "bg-sky-50 border-sky-200 text-sky-600" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}
            >
              <Filter className="w-3.5 h-3.5" /> Filters {hasFilters && <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />}
            </button>
            <Button variant="outline" size="sm" onClick={clearOld}>
              <Trash2 className="w-3 h-3 mr-1" /> Hapus >30 hari
            </Button>
            <span className="text-[11px] text-slate-400 ml-auto">{total.toLocaleString()} logs</span>
          </div>

          {showFilters && (
            <Card className="p-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <select value={methodFilter} onChange={(e) => { setMethodFilter(e.target.value); setPage(0); }}
                  className="text-xs rounded-lg border border-slate-200 px-3 py-2 bg-white">
                  <option value="">Semua Method</option>
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                  <option value="PATCH">PATCH</option>
                </select>
                <input placeholder="Path filter..." value={pathFilter} onChange={(e) => { setPathFilter(e.target.value); setPage(0); }}
                  className="text-xs rounded-lg border border-slate-200 px-3 py-2" />
                <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                  className="text-xs rounded-lg border border-slate-200 px-3 py-2 bg-white">
                  <option value="">Semua Status</option>
                  <option value="200">200 OK</option>
                  <option value="201">201 Created</option>
                  <option value="400">400 Bad Request</option>
                  <option value="401">401 Unauthorized</option>
                  <option value="402">402 Payment Required</option>
                  <option value="403">403 Forbidden</option>
                  <option value="404">404 Not Found</option>
                  <option value="429">429 Rate Limited</option>
                  <option value="500">500 Server Error</option>
                  <option value="503">503 Unavailable</option>
                </select>
                <input placeholder="Search path/body/response..." value={searchFilter}
                  onChange={(e) => { setSearchFilter(e.target.value); setPage(0); }}
                  onKeyDown={(e) => e.key === "Enter" && loadLogs()}
                  className="text-xs rounded-lg border border-slate-200 px-3 py-2" />
              </div>
              {hasFilters && (
                <button onClick={resetFilters} className="text-[10px] text-sky-500 hover:text-sky-600 mt-2 font-semibold">
                  Reset filters
                </button>
              )}
            </Card>
          )}

          {/* Log entries */}
          {loading ? (
            <div className="py-20 text-center"><RefreshCw className="w-5 h-5 text-slate-300 animate-spin mx-auto" /></div>
          ) : logs.length === 0 ? (
            <Card className="p-12 text-center text-sm text-slate-400">Tidak ada logs ditemukan.</Card>
          ) : (
            <div className="space-y-1">
              {logs.map((log) => (
                <button
                  key={log._id}
                  onClick={() => setDetail(log)}
                  className="w-full text-left hover:bg-slate-50 transition rounded-xl border border-slate-100 p-3 group"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <MethodBadge method={log.method} />
                    <StatusBadge code={log.status} />
                    <span className="font-mono text-xs text-slate-600 truncate flex-1 min-w-0">{log.path}</span>
                    <span className="text-[10px] text-slate-400 shrink-0">{log.duration_ms}ms</span>
                    <span className="text-[10px] text-slate-300 shrink-0">{new Date(log.created_at).toLocaleTimeString("id-ID")}</span>
                  </div>
                  {log.error && (
                    <p className="text-[10px] text-red-500 mt-1 truncate">{log.error}</p>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 pt-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <span className="text-xs text-slate-400 px-2">Page {page + 1} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          )}
        </div>
      )}

      {/* Detail modal */}
      {detail && <LogDetail log={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
